import type { MarkdownPreviewStyle } from "./markdownPreviewStyle.js";

const SANS =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const SERIF = 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif';
const MONO_MENLO = '"Consolas", "Monaco", "Menlo", monospace';
const MONO_FIRA = '"Fira Code", "Fira Mono", ui-monospace, monospace';
const MONO_JETBRAINS = '"JetBrains Mono", ui-monospace, monospace';
const MONO_SOURCE = '"Source Code Pro", ui-monospace, monospace';

const HEADING_SCALE: Record<
  MarkdownPreviewStyle["headingScale"],
  readonly [string, string, string, string, string, string]
> = {
  compact: ["1.22em", "1.1em", "1.04em", "1em", "0.96em", "0.93em"],
  balanced: ["1.54em", "1.31em", "1.14em", "1.05em", "1em", "0.95em"],
  editorial: ["1.95em", "1.48em", "1.22em", "1.08em", "1.02em", "0.98em"],
  prominent: ["2.2em", "1.62em", "1.32em", "1.12em", "1.04em", "1em"],
};

function headingFontFamily(role: MarkdownPreviewStyle["headingFont"]): string {
  if (role === "sans") return SANS;
  if (role === "serif") return SERIF;
  return "inherit";
}

function monoStack(preset: MarkdownPreviewStyle["codeFont"]): string {
  switch (preset) {
    case "fira":
      return MONO_FIRA;
    case "jetbrains":
      return MONO_JETBRAINS;
    case "source":
      return MONO_SOURCE;
    default:
      return MONO_MENLO;
  }
}

/** Sets CSS variables + data attributes on `.npp-md-preview` from saved style. */
export function applyMarkdownPreviewTheme(root: HTMLElement, s: MarkdownPreviewStyle): void {
  const [h1, h2, h3, h4, h5, h6] = HEADING_SCALE[s.headingScale];

  root.dataset.nppMdScale = s.fontScale;
  root.dataset.nppMdWidth = s.contentWidth;
  root.dataset.nppMdProse = s.proseFont;
  root.dataset.nppMdTable = s.tableStyle;
  root.dataset.nppMdLink = s.linkTone;

  const st = root.style;
  st.setProperty("--npp-md-h1-size", h1);
  st.setProperty("--npp-md-h2-size", h2);
  st.setProperty("--npp-md-h3-size", h3);
  st.setProperty("--npp-md-h4-size", h4);
  st.setProperty("--npp-md-h5-size", h5);
  st.setProperty("--npp-md-h6-size", h6);
  st.setProperty("--npp-md-heading-font", headingFontFamily(s.headingFont));
  st.setProperty("--npp-md-heading-line-height", s.headingScale === "compact" ? "1.22" : "1.28");
  st.setProperty(
    "--npp-md-heading-margin-block",
    s.headingScale === "compact" ? "0.55rem 0 0.28rem" : "0.75rem 0 0.35rem",
  );

  const pMargin =
    s.paragraphSpacing === "compact" ? "0.2rem 0" : s.paragraphSpacing === "relaxed" ? "0.55rem 0" : "0.35rem 0";
  st.setProperty("--npp-md-p-margin", pMargin);

  const listPad =
    s.listDensity === "compact" ? "1.1rem" : s.listDensity === "relaxed" ? "1.55rem" : "1.35rem";
  st.setProperty("--npp-md-list-pad-left", listPad);
  const listMargin =
    s.listDensity === "compact" ? "0.22rem 0" : s.listDensity === "relaxed" ? "0.5rem 0" : "0.35rem 0";
  st.setProperty("--npp-md-list-margin", listMargin);

  const codeEm = s.codeSize === "small" ? "0.88em" : s.codeSize === "large" ? "1em" : "0.93em";
  st.setProperty("--npp-md-code-font-size", codeEm);
  st.setProperty("--npp-md-pre-font-size", s.codeSize === "small" ? "0.78em" : s.codeSize === "large" ? "0.9em" : "0.82em");
  st.setProperty("--npp-md-code-font-family", monoStack(s.codeFont));

  switch (s.codeBlockTone) {
    case "plain":
      st.setProperty("--npp-md-pre-bg", "#fff");
      st.setProperty("--npp-md-pre-border", "1px solid var(--npp-border)");
      st.setProperty("--npp-md-inline-code-bg", "#f5f5f5");
      break;
    case "high-contrast":
      st.setProperty("--npp-md-pre-bg", "#e8ecf2");
      st.setProperty("--npp-md-pre-border", "1px solid var(--npp-border-strong)");
      st.setProperty("--npp-md-inline-code-bg", "#dde4ee");
      break;
    default:
      st.setProperty("--npp-md-pre-bg", "#f6f8fa");
      st.setProperty("--npp-md-pre-border", "1px solid var(--npp-border)");
      st.setProperty("--npp-md-inline-code-bg", "#f0f0f0");
  }

  switch (s.blockquoteTone) {
    case "subtle":
      st.setProperty("--npp-md-bq-border", "3px solid #ddd");
      st.setProperty("--npp-md-bq-color", "var(--npp-muted-text)");
      break;
    case "strong":
      st.setProperty("--npp-md-bq-border", "4px solid var(--npp-border-strong)");
      st.setProperty("--npp-md-bq-color", "var(--npp-text)");
      break;
    default:
      st.setProperty("--npp-md-bq-border", "3px solid #ccc");
      st.setProperty("--npp-md-bq-color", "#444");
  }

  switch (s.linkTone) {
    case "accent":
      st.setProperty("--npp-md-link-color", "var(--npp-text)");
      st.setProperty("--npp-md-link-decoration", "underline");
      st.setProperty("--npp-md-link-weight", "600");
      break;
    case "neutral-underline":
      st.setProperty("--npp-md-link-color", "var(--npp-muted-text)");
      st.setProperty("--npp-md-link-decoration", "underline");
      st.setProperty("--npp-md-link-weight", "500");
      break;
    default:
      st.setProperty("--npp-md-link-color", "#0969da");
      st.setProperty("--npp-md-link-decoration", "none");
      st.setProperty("--npp-md-link-weight", "inherit");
  }

  const cellPad =
    s.tableDensity === "compact" ? "0.15rem 0.35rem" : s.tableDensity === "comfortable" ? "0.35rem 0.6rem" : "0.25rem 0.5rem";
  st.setProperty("--npp-md-table-cell-pad", cellPad);
}
